import { makeApi, Zodios, type ZodiosOptions } from "@zodios/core";
import { z } from "zod";

type UserProfileInfo = {
  targetSchoolGroup?: TargetSchoolGroup | undefined;
  gender: Gender;
  /**
   * The IDs of the contests the user is registered to
   */
  registeredContestIds: Array<number>;
  id: number;
  levelId?: number | undefined;
  proposedLevelName?: string | undefined;
  firstName: string;
  lastName?: string | undefined;
  fullName: string;
  emailValidated: boolean;
  schoolId?: number | undefined;
  proposedSchoolName?: string | undefined;
  username: string;
  phone: string;
  email: string;
  birthDate: string;
  schoolName?: string | undefined;
  canDeleteExams: boolean;
  canUpdateExams: boolean;
  isAdmin: boolean;
  isAtLeastOperator: boolean;
  isProfessor: boolean;
  isStudent: boolean;
  hasScholarship: boolean;
};
type TargetSchoolGroup =
  /**
   * @enum TOP 3 (HEC Paris, ESSEC, ESCP), TOP 5 (EDHEC, EMLYON), TOP 7 (SKEMA, AUDENCIA), TOP 10 (NEOMA, GEM, TBS), TOP 12 (KEDGE, RSB), TOP 15 (MBS, BSB, ICN), TOP 18 (IMT-BS, Excelia, EM Strasbourg), TOP 20 (EM Normandie, ISC Paris), TOP 24 (INSEEC, ESC Clermont, SCBS, BBS)
   */
  | "TOP 3 (HEC Paris, ESSEC, ESCP)"
  | "TOP 5 (EDHEC, EMLYON)"
  | "TOP 7 (SKEMA, AUDENCIA)"
  | "TOP 10 (NEOMA, GEM, TBS)"
  | "TOP 12 (KEDGE, RSB)"
  | "TOP 15 (MBS, BSB, ICN)"
  | "TOP 18 (IMT-BS, Excelia, EM Strasbourg)"
  | "TOP 20 (EM Normandie, ISC Paris)"
  | "TOP 24 (INSEEC, ESC Clermont, SCBS, BBS)";
type Gender =
  /**
   * @enum M, F
   */
  "M" | "F";
type UpdateProfileDto = Partial<{
  /**
   * The school id the user will be enrolled in. If not provided, the user must provide a proposed school name
   */
  schoolId: number;
  /**
   * The level id the user will be enrolled in. If not provided, the user must provide a proposed level name
   */
  levelId: number;
  targetSchoolGroup: TargetSchoolGroupEnum;
  gender: GenderEnum;
  firstName: string;
  lastName: string;
  proposedSchoolName: string;
  proposedLevelName: string;
  /**
   * @minLength 3
   * @maxLength 30
   */
  username: string;
  phone: string;
  birthDate: string;
  hasScholarship: boolean;
  registeredContestIds: Array<number>;
}>;
type TargetSchoolGroupEnum =
  /**
   * @enum TOP 3 (HEC Paris, ESSEC, ESCP), TOP 5 (EDHEC, EMLYON), TOP 7 (SKEMA, AUDENCIA), TOP 10 (NEOMA, GEM, TBS), TOP 12 (KEDGE, RSB), TOP 15 (MBS, BSB, ICN), TOP 18 (IMT-BS, Excelia, EM Strasbourg), TOP 20 (EM Normandie, ISC Paris), TOP 24 (INSEEC, ESC Clermont, SCBS, BBS)
   */
  | "TOP 3 (HEC Paris, ESSEC, ESCP)"
  | "TOP 5 (EDHEC, EMLYON)"
  | "TOP 7 (SKEMA, AUDENCIA)"
  | "TOP 10 (NEOMA, GEM, TBS)"
  | "TOP 12 (KEDGE, RSB)"
  | "TOP 15 (MBS, BSB, ICN)"
  | "TOP 18 (IMT-BS, Excelia, EM Strasbourg)"
  | "TOP 20 (EM Normandie, ISC Paris)"
  | "TOP 24 (INSEEC, ESC Clermont, SCBS, BBS)";
type GenderEnum =
  /**
   * @enum M, F
   */
  "M" | "F";
type SignUpDto = {
  schoolId?: /**
   * The school id the user will be enrolled in. If not provided, the user must provide a proposed school name
   */
  number | undefined;
  targetSchoolGroup?: TargetSchoolGroupEnum | undefined;
  levelId?: /**
   * The level id the user will be enrolled in. If not provided, the user must provide a proposed level name
   */
  number | undefined;
  gender: GenderEnum;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  termsAccepted: boolean;
  proposedSchoolName?: string | undefined;
  proposedLevelName?: string | undefined;
  /**
   * @minLength 3
   * @maxLength 30
   */
  username: string;
  phone: string;
  birthDate: string;
  hasScholarship: boolean;
  registeredContestIds?: Array<number> | undefined;
};
type Chapter = {
  id: number;
  name: string;
  subjectName: string;
  subjectId: number;
  description?: string | undefined;
  courses: Array<ShortCourse>;
  videoId?: string | undefined;
  levels: Array<Level>;
  slug?: string | undefined;
  introVideoUrl?: string | undefined;
};
type ShortCourse = {
  id: number;
  name: string;
  description?: string | undefined;
  slug: string;
  courseVideoDurationInSeconds?: number | undefined;
  exerciseVideoDurationInSeconds?: number | undefined;
};
type Level = {
  id: number;
  name: string;
};
type AnyExam = {
  year?: number | undefined;
  levels?: Array<Level> | undefined;
  levelNames?: string | undefined;
  id: number;
  name: string;
  title?: string | undefined;
  slug: string;
  signedUrl: string;
  subjectName?: string | undefined;
  subjectId?: number | undefined;
  contestName?: string | undefined;
  /**
   * @enum exam, oral-exam
   */
  type: "exam" | "oral-exam";
  status: {};
  isPublic: boolean;
};
type PaginatedAnyExam = {
  data: Array<AnyExam>;
  total: number;
};
type PublicContestExam = {
  year?: number | undefined;
  levels: Array<Level>;
  levelNames: string;
  id: number;
  name: string;
  title: string;
  slug: string;
  signedUrl: string;
  subjectName: string;
  subjectId: number;
  contestName?: string | undefined;
  /**
   * @enum exam, oral-exam
   */
  type: "exam" | "oral-exam";
};
type PaginatedPublicContestExam = {
  data: Array<PublicContestExam>;
  total: number;
};
type PaginatedUserExam = {
  data: Array<UserExam>;
  total: number;
};
type UserExam = {
  id: number;
  name: string;
  slug: string;
  signedUrl: string;
};
type ExamOrExerciseSectionWithImage = {
  type: ExerciseOrExamSectionType;
  language?: /**
   * The language of the code
   */
  ProgrammingLanguage | undefined;
  imageUrl: string;
  answerImageUrl?: string | undefined;
  isCoding: boolean;
  contentLatex?: string | undefined;
  answerContentLatex?: string | undefined;
  indicationContentLatex?: string | undefined;
  sectionId: number;
  percentage: number;
  questionNumber?: string | undefined;
  prefix?: string | undefined;
  subpartTitle?: string | undefined;
  subpartName?: string | undefined;
  answerCode?: string | undefined;
};
type ExerciseOrExamSectionType =
  /**
   * @enum question, questionGroup, appendix, other, introduction, header, footer, context, examSubpartTitle, examTitle
   */
  | "question"
  | "questionGroup"
  | "appendix"
  | "other"
  | "introduction"
  | "header"
  | "footer"
  | "context"
  | "examSubpartTitle"
  | "examTitle";
type ProgrammingLanguage =
  /**
   * The languages of the exam or exercise
   *
   * @enum python, ocaml, sql, c, scilab
   */
  "python" | "ocaml" | "sql" | "c" | "scilab";
type ExamCode = {
  /**
   * The language of the code
   */
  language: ProgrammingLanguage;
  /**
   * The code of the exam or exercise
   *
   * @example "print(\"Hello, world!\")"
   */
  code: string;
};
type Task = {
  /**
   * The type of the task
   */
  type: TaskType;
  /**
   * The status of the task
   */
  status: TaskStatus;
  id: number;
  createdAt: string;
  updatedAt: string;
  exerciseOrExamId?: number | undefined;
  userId: number;
  durationMs?: number | undefined;
  inputTokens?: number | undefined;
  outputTokens?: number | undefined;
  cacheTokens?: number | undefined;
  model?: string | undefined;
  progress?: number | undefined;
};
type TaskType =
  /**
   * The type of the task
   *
   * @enum exam_file_segmentation, exam_solutions_file_segmentation, exam_sections_latex_extraction
   */
  | "exam_file_segmentation"
  | "exam_solutions_file_segmentation"
  | "exam_sections_latex_extraction";
type TaskStatus =
  /**
   * The status of the task
   *
   * @enum in_queue, running, success, error
   */
  "in_queue" | "running" | "success" | "error";
type ExamOrExercise = {
  /**
   * The status of the exam or exercise
   */
  status: ExamStatus;
  languages?: /**
   * The languages of the exam or exercise
   *
   * @example ["python","ocaml"]
   */
  Array<ProgrammingLanguage> | undefined;
  id: number;
  name?: string | undefined;
  title?: string | undefined;
  /**
   * @enum exercise, exam, oral-exam
   */
  type: "exercise" | "exam" | "oral-exam";
  subjectId?: number | undefined;
  subjectName?: string | undefined;
  slug: string;
  createdAt: string;
  updatedAt: string;
  documentUrlAsImage: string;
  contestId?: number | undefined;
  contestName?: string | undefined;
  year?: number | undefined;
  durationInHours?: string | undefined;
  sections: Array<ExamOrExerciseSectionWithImage>;
  levels: Array<Level>;
  levelNames: string;
  canEdit: boolean;
  canDelete: boolean;
  isPublic: boolean;
  codes?: Array<ExamCode> | undefined;
  tasks?: Array<Task> | undefined;
  isSegmentingWithAiOrInQueue: boolean;
  isSegmentingSolutionsFileWithAiOrInQueue: boolean;
  isExtractingSectionsLatexWithAiOrInQueue: boolean;
};
type ExamStatus =
  /**
   * The status of the exam or exercise
   *
   * @enum SEG_TODO, SEG_PENDING, SEG_DONE, SEG_ERROR, SEG_VERIFIED_PREFIX_TODO, PREFIX_PENDING, PREFIX_DONE, PREFIX_ERROR, PREFIX_VERIFIED_CORRIGE_TODO, CORRIGE_PENDING, CORRIGE_DONE, CORRIGE_ERROR, CORRIGE_VERIFIED_ANSWERS_TODO, ANSWERS_PENDING, ANSWERS_DONE, ANSWERS_ERROR, ANSWERS_VERIFIED_READY
   */
  | "SEG_TODO"
  | "SEG_PENDING"
  | "SEG_DONE"
  | "SEG_ERROR"
  | "SEG_VERIFIED_PREFIX_TODO"
  | "PREFIX_PENDING"
  | "PREFIX_DONE"
  | "PREFIX_ERROR"
  | "PREFIX_VERIFIED_CORRIGE_TODO"
  | "CORRIGE_PENDING"
  | "CORRIGE_DONE"
  | "CORRIGE_ERROR"
  | "CORRIGE_VERIFIED_ANSWERS_TODO"
  | "ANSWERS_PENDING"
  | "ANSWERS_DONE"
  | "ANSWERS_ERROR"
  | "ANSWERS_VERIFIED_READY";
type ExamOrExerciseSectionFull = {
  type: ExerciseOrExamSectionType;
  language?: /**
   * The language of the code
   */
  ProgrammingLanguage | undefined;
  answerImageUrl?: string | undefined;
  solutionSectionContentLatex?: string | undefined;
  solutionSectionCodeAnswer?: string | undefined;
  solutionsSectionId?: number | undefined;
  solutionSectionContainsSchemaOrDrawing?: boolean | undefined;
  solutionSectionSchemaDescription?: string | undefined;
  containsSchemaOrDrawing: boolean;
  schemaDescription?: string | undefined;
  subpartTitle?: string | undefined;
  isCoding: boolean;
  contentLatex?: string | undefined;
  answerContentLatex?: string | undefined;
  indicationContentLatex?: string | undefined;
  sectionId: number;
  percentage: number;
  imageUrl?: string | undefined;
  questionNumber?: string | undefined;
  prefix?: string | undefined;
  subpartName?: string | undefined;
  answerCode?: string | undefined;
};
type SolutionsFileSection = {
  type: ExerciseOrExamSolutionsFileSectionType;
  percentage: number;
  id: number;
  sectionId?: number | undefined;
};
type ExerciseOrExamSolutionsFileSectionType =
  /**
   * @enum solution, other
   */
  "solution" | "other";
type HasSolutionsFileResult = {
  hasSolutionsFile: boolean;
  solutionsFilePagesAsImages?: Array<string> | undefined;
  solutionsFileSections?: Array<SolutionsFileSection> | undefined;
};
type UpdateSectionDto = Partial<{
  /**
   * The type of the section of the exam or exercise
   */
  type: ExerciseOrExamSectionType;
  /**
   * The language of the code (must be no-code if no language)
   */
  language: ProgrammingLanguageForUpdate;
  answerContentLatex: string;
  contentLatex: string;
  containsSchemaOrDrawing: boolean;
  schemaDescription: string;
  subpartTitle: string;
  subpartName: string;
  indicationContentLatex: string;
  answerCode: string;
  questionNumber: string;
  prefix: string;
}>;
type ProgrammingLanguageForUpdate =
  /**
   * The language of the code (must be no-code if no language)
   *
   * @enum python, ocaml, sql, c, scilab, no-code
   */
  "python" | "ocaml" | "sql" | "c" | "scilab" | "no-code";
type ExamOrExerciseSectionForUpdate = {
  type: ExerciseOrExamSectionType;
  language?: /**
   * The language of the code (must be no-code if no language)
   */
  ProgrammingLanguageForUpdate | undefined;
  /**
   * Percentage of where the section ends in the exam
   *
   * @example 15.45
   * @minimum 0
   * @maximum 100
   */
  percentage: number;
  mode?: ExamOrExerciseSectionForUpdateMode | undefined;
  contentLatex?: string | undefined;
  answerContentLatex?: string | undefined;
  indicationContentLatex?: string | undefined;
  questionNumber?: string | undefined;
  prefix?: string | undefined;
  subpartTitle?: string | undefined;
  subpartName?: string | undefined;
  answerCode?: string | undefined;
  sectionId?: /**
   * @minimum 1
   */
  number | undefined;
};
type ExamOrExerciseSectionForUpdateMode =
  /**
   * @enum is_added, is_updated, is_deleted
   */
  "is_added" | "is_updated" | "is_deleted";
type UpdateExamOrExerciseDto = Partial<{
  status: ExamStatus;
  type: ExerciseOrExamType;
  title: string;
  name: string;
  /**
   * @maximum 2025
   */
  year: number;
  durationInHours: string;
  annotatorComment: string;
  contestId: number;
  slug: string;
  sqlQuery: string;
  sections: Array<ExamOrExerciseSectionForUpdate>;
  levelIds: Array<number>;
  codes: Array<ExamCode>;
}>;
type ExerciseOrExamType =
  /**
   * @enum exercise, exam, oral-exam
   */
  "exercise" | "exam" | "oral-exam";
type SolutionsFileSectionForUpdate = {
  type: ExerciseOrExamSolutionsFileSectionType;
  mode?: SolutionsFileSectionForUpdateMode | undefined;
  id?: /**
   * @minimum 1
   */
  number | undefined;
  percentage: number;
  sectionId?: number | undefined;
};
type SolutionsFileSectionForUpdateMode =
  /**
   * @enum is_added, is_updated, is_deleted
   */
  "is_added" | "is_updated" | "is_deleted";
type ExamOrExerciseSolutionsFileSectionsDto = {
  sections: Array<SolutionsFileSectionForUpdate>;
};
type TargetSchoolGroupsEntity = {
  targetSchoolGroups: Array<TargetSchoolGroup>;
};
type School = {
  name: string;
  id: number;
  levels: Array<Level>;
  nationalId?: string | undefined;
  city?: string | undefined;
  zipCode?: string | undefined;
};
type StudentGroup = {
  userId: number;
  students: Array<Student>;
  name: string;
  id: number;
};
type Student = {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
};
type ExamSharing = {
  examId: number;
  examName: string;
  examSlug: string;
  studentGroups: Array<StudentGroupWithNoDetails>;
};
type StudentGroupWithNoDetails = {
  name: string;
  id: number;
};
type StudentProgressForExam = {
  userId: number;
  firstName: string;
  lastName?: string | undefined;
  answers: Array<Answer>;
};
type Answer = {
  answerId: number;
  answer: string;
  sectionId: number;
};
type CodeSubmissionResultEntity = {
  status: CodeSubmissionStatus;
  id: number;
  sectionId: number;
  stdOut: string;
  stdErr: string;
};
type CodeSubmissionStatus =
  /**
   * @enum pending, success, error
   */
  "pending" | "success" | "error";
type ConversationMessage = {
  /**
   * The role of the user conversation message
   */
  role: UserConversationMessageRole;
  id: string;
  content: string;
  createdAt: string;
};
type UserConversationMessageRole =
  /**
   * The role of the user conversation message
   *
   * @enum assistant, user
   */
  "assistant" | "user";
type FullUserConversation = {
  id: string;
  title: string;
  createdAt: string;
  messages: Array<ConversationMessage>;
};
type AiInterview = {
  /**
   * The status of an ai interview
   */
  status: AiInterviewStatus;
  id: number;
  name: string;
  description?: string | undefined;
  price: number;
  dailyRoomUrl?: string | undefined;
  aiInterviewTemplateId: number;
  transcription?: Array<AiInterviewMessage> | undefined;
  evaluation?: string | undefined;
  startedAt?: string | undefined;
};
type AiInterviewStatus =
  /**
   * The status of an ai interview
   *
   * @enum pending, in_progress, completed, error
   */
  "pending" | "in_progress" | "completed" | "error";
type AiInterviewMessage = {
  /**
   * The user who sent the message
   *
   * @enum bot, interviewee
   */
  user: "bot" | "interviewee";
  message: string;
  createdAt: string;
};
type AiInterviewWithDetails = {
  /**
   * The status of an ai interview
   */
  status: AiInterviewStatus;
  id: number;
  name: string;
  description?: string | undefined;
  price: number;
  dailyRoomUrl?: string | undefined;
  createdAt: string;
  updatedAt: string;
  startedAt?: string | undefined;
  finishedAt?: string | undefined;
  pid?: string | undefined;
  logPath?: string | undefined;
  pythonScript?: string | undefined;
  envVariables?: {} | undefined;
  logs?: string | undefined;
  userId: number;
  userName: string;
  userOrderItemId?: number | undefined;
  productId?: number | undefined;
  aiInterviewTemplateId: number;
  currency: string;
  transcription?: Array<AiInterviewMessage> | undefined;
  evaluation?: string | undefined;
  evaluationInstructions?: string | undefined;
};
type PaginatedAiInterviewWithDetails = {
  data: Array<AiInterviewWithDetails>;
  total: number;
};
type UserOrder = {
  /**
   * The status of the user order
   */
  status: UserOrderStatus;
  id: number;
  reference: string;
  items: Array<UserOrderItem>;
  clientSecret?: string | undefined;
};
type UserOrderStatus =
  /**
   * The status of the user order
   *
   * @enum processing_payment, payment_failed, payment_succeeded, refunded, checkout_session_creation_error
   */
  | "processing_payment"
  | "payment_failed"
  | "payment_succeeded"
  | "refunded"
  | "checkout_session_creation_error";
type UserOrderItem = {
  productId: number;
  quantity: number;
  aiInterviewTemplateId?: number | undefined;
  productName: string;
  unitPrice: number;
  description?: string | undefined;
};
type UpdateUserCartDto = {
  items: Array<CartItemDto>;
};
type CartItemDto = {
  aiInterviewTemplateId?: number | undefined;
  productId: number;
  /**
   * @minimum 1
   */
  quantity: number;
};

const TargetSchoolGroup = z.enum([
  "TOP 3 (HEC Paris, ESSEC, ESCP)",
  "TOP 5 (EDHEC, EMLYON)",
  "TOP 7 (SKEMA, AUDENCIA)",
  "TOP 10 (NEOMA, GEM, TBS)",
  "TOP 12 (KEDGE, RSB)",
  "TOP 15 (MBS, BSB, ICN)",
  "TOP 18 (IMT-BS, Excelia, EM Strasbourg)",
  "TOP 20 (EM Normandie, ISC Paris)",
  "TOP 24 (INSEEC, ESC Clermont, SCBS, BBS)",
]);
const Gender = z.enum(["M", "F"]);
const UserProfileInfo: z.ZodType<UserProfileInfo> = z
  .object({
    targetSchoolGroup: TargetSchoolGroup.optional(),
    gender: Gender,
    registeredContestIds: z
      .array(z.number())
      .describe("The IDs of the contests the user is registered to"),
    id: z.number(),
    levelId: z.number().optional(),
    proposedLevelName: z.string().optional(),
    firstName: z.string(),
    lastName: z.string().optional(),
    fullName: z.string(),
    emailValidated: z.boolean(),
    schoolId: z.number().optional(),
    proposedSchoolName: z.string().optional(),
    username: z.string(),
    phone: z.string(),
    email: z.string(),
    birthDate: z.string(),
    schoolName: z.string().optional(),
    canDeleteExams: z.boolean(),
    canUpdateExams: z.boolean(),
    isAdmin: z.boolean(),
    isAtLeastOperator: z.boolean(),
    isProfessor: z.boolean(),
    isStudent: z.boolean(),
    hasScholarship: z.boolean(),
  })
  .strict()
  .passthrough();
const Username = z
  .object({ username: z.string(), id: z.number() })
  .strict()
  .passthrough();
const TargetSchoolGroupEnum = z.enum([
  "TOP 3 (HEC Paris, ESSEC, ESCP)",
  "TOP 5 (EDHEC, EMLYON)",
  "TOP 7 (SKEMA, AUDENCIA)",
  "TOP 10 (NEOMA, GEM, TBS)",
  "TOP 12 (KEDGE, RSB)",
  "TOP 15 (MBS, BSB, ICN)",
  "TOP 18 (IMT-BS, Excelia, EM Strasbourg)",
  "TOP 20 (EM Normandie, ISC Paris)",
  "TOP 24 (INSEEC, ESC Clermont, SCBS, BBS)",
]);
const GenderEnum = z.enum(["M", "F"]);
const UpdateProfileDto: z.ZodType<UpdateProfileDto> = z
  .object({
    schoolId: z
      .number()
      .describe(
        "The school id the user will be enrolled in. If not provided, the user must provide a proposed school name"
      ),
    levelId: z
      .number()
      .describe(
        "The level id the user will be enrolled in. If not provided, the user must provide a proposed level name"
      ),
    targetSchoolGroup: TargetSchoolGroupEnum,
    gender: GenderEnum,
    firstName: z.string(),
    lastName: z.string(),
    proposedSchoolName: z.string(),
    proposedLevelName: z.string(),
    username: z.string().min(3).max(30),
    phone: z.string(),
    birthDate: z.string(),
    hasScholarship: z.boolean(),
    registeredContestIds: z.array(z.number()),
  })
  .partial()
  .strict()
  .passthrough();
const SignUpDto: z.ZodType<SignUpDto> = z
  .object({
    schoolId: z
      .number()
      .describe(
        "The school id the user will be enrolled in. If not provided, the user must provide a proposed school name"
      )
      .optional(),
    targetSchoolGroup: TargetSchoolGroupEnum.optional(),
    levelId: z
      .number()
      .describe(
        "The level id the user will be enrolled in. If not provided, the user must provide a proposed level name"
      )
      .optional(),
    gender: GenderEnum,
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    password: z.string(),
    termsAccepted: z.boolean(),
    proposedSchoolName: z.string().optional(),
    proposedLevelName: z.string().optional(),
    username: z.string().min(3).max(30),
    phone: z.string(),
    birthDate: z.string(),
    hasScholarship: z.boolean(),
    registeredContestIds: z.array(z.number()).optional(),
  })
  .strict()
  .passthrough();
const SignInDto = z
  .object({
    email: z.string().email().describe("The user email"),
    password: z.string(),
  })
  .strict()
  .passthrough();
const SignInResponseDto = z
  .object({ emailValidated: z.boolean(), role: z.string() })
  .strict()
  .passthrough();
const VerifyEmailDto = z.object({ token: z.string() }).strict().passthrough();
const SendVerificationEmailResponse = z
  .object({
    success: z.boolean(),
    isEmailAlreadyValidated: z.boolean().optional(),
  })
  .strict()
  .passthrough();
const CheckEmailAvailableDto = z
  .object({ email: z.string().email() })
  .strict()
  .passthrough();
const CheckEmailResponseDto = z
  .object({ emailExists: z.boolean() })
  .strict()
  .passthrough();
const CheckUsernameAvailableDto = z
  .object({ username: z.string() })
  .strict()
  .passthrough();
const CheckUsernameResponseDto = z
  .object({ usernameExists: z.boolean() })
  .strict()
  .passthrough();
const AskResetPasswordDto = z
  .object({ email: z.string().email() })
  .strict()
  .passthrough();
const ResetPasswordDto = z
  .object({ token: z.string(), password: z.string() })
  .strict()
  .passthrough();
const UpdateEmailDto = z
  .object({ newEmail: z.string().email() })
  .strict()
  .passthrough();
const UpdatePasswordDto = z
  .object({
    newPassword: z.string(),
    confirmedNewPassword: z.string(),
    currentPassword: z.string(),
  })
  .strict()
  .passthrough();
const AuthenticationStatusEntity = z
  .object({
    isAuthenticated: z.boolean(),
    isEmailVerified: z.boolean().optional(),
    email: z.string().optional(),
  })
  .strict()
  .passthrough();
const CreateCourseDto = z
  .object({
    courseVideoId: z.string().describe("The VideoPlus video id"),
    exerciseVideoId: z.string().describe("The VideoPlus video id").optional(),
    name: z.string(),
    description: z.string(),
    subjectId: z.number(),
    chapterId: z.number(),
    orderInChapter: z.number().gte(1),
  })
  .strict()
  .passthrough();
const CreateCourseResponseDto = z
  .object({ courseId: z.number() })
  .strict()
  .passthrough();
const Course = z
  .object({
    id: z.number(),
    subjectId: z.number(),
    name: z.string(),
    description: z.string().optional(),
    courseThumbnailUrl: z.string().optional(),
    courseVideoUrl: z.string().optional(),
    exerciseVideoUrl: z.string().optional(),
    courseVideoId: z.string().optional(),
    exerciseVideoId: z.string().optional(),
    chapterId: z.number().optional(),
    orderInChapter: z.number().optional(),
  })
  .strict()
  .passthrough();
const UpdateCourseDto = z
  .object({
    courseVideoId: z.string().describe("The VideoPlus video id"),
    exerciseVideoId: z.string().describe("The VideoPlus video id").optional(),
    name: z.string(),
    description: z.string(),
    chapterId: z.number(),
    orderInChapter: z.number().gte(1),
  })
  .strict()
  .passthrough();
const CreateLevelDto = z
  .object({ name: z.string().min(3), levelGroupId: z.number().optional() })
  .strict()
  .passthrough();
const Level: z.ZodType<Level> = z
  .object({ id: z.number(), name: z.string() })
  .strict()
  .passthrough();
const LevelGroup = z
  .object({
    id: z.number(),
    name: z.string(),
    mustSpecifyTargetSchoolGroup: z.boolean(),
    mustSpecifyRegisteredContests: z.boolean(),
  })
  .strict()
  .passthrough();
const CreateLevelGroupDto = z
  .object({
    name: z.string().min(3),
    isActive: z.boolean(),
    mustSpecifyTargetSchoolGroup: z.boolean(),
    mustSpecifyRegisteredContests: z.boolean(),
  })
  .strict()
  .passthrough();
const UpdateLevelGroupDto = z
  .object({
    name: z.string().min(3),
    isActive: z.boolean(),
    mustSpecifyTargetSchoolGroup: z.boolean(),
    mustSpecifyRegisteredContests: z.boolean(),
  })
  .partial()
  .strict()
  .passthrough();
const UpdateLevelDto = z
  .object({ name: z.string(), levelGroupId: z.number() })
  .partial()
  .strict()
  .passthrough();
const MergeLevelsDto = z
  .object({ sourceLevelId: z.number(), targetLevelId: z.number() })
  .strict()
  .passthrough();
const CreateContestDto = z
  .object({ name: z.string().min(3) })
  .strict()
  .passthrough();
const Contest = z
  .object({
    id: z.number(),
    name: z.string(),
    shortName: z.string().optional(),
  })
  .strict()
  .passthrough();
const UpdateContestDto = z
  .object({ name: z.string().min(3) })
  .partial()
  .strict()
  .passthrough();
const MergeContestsDto = z
  .object({ sourceContestId: z.number(), targetContestId: z.number() })
  .strict()
  .passthrough();
const Subject = z
  .object({ id: z.number(), name: z.string() })
  .strict()
  .passthrough();
const CreateChapterDto = z
  .object({
    name: z.string().min(3),
    description: z.string().min(3),
    subjectId: z.number(),
    videoId: z.string().min(3),
  })
  .strict()
  .passthrough();
const ShortCourse: z.ZodType<ShortCourse> = z
  .object({
    id: z.number(),
    name: z.string(),
    description: z.string().optional(),
    slug: z.string(),
    courseVideoDurationInSeconds: z.number().optional(),
    exerciseVideoDurationInSeconds: z.number().optional(),
  })
  .strict()
  .passthrough();
const Chapter: z.ZodType<Chapter> = z
  .object({
    id: z.number(),
    name: z.string(),
    subjectName: z.string(),
    subjectId: z.number(),
    description: z.string().optional(),
    courses: z.array(ShortCourse),
    videoId: z.string().optional(),
    levels: z.array(Level),
    slug: z.string().optional(),
    introVideoUrl: z.string().optional(),
  })
  .strict()
  .passthrough();
const UpdateChapterDto = z
  .object({
    name: z.string().min(3),
    description: z.string().min(3),
    videoId: z.string().min(3),
  })
  .strict()
  .passthrough();
const ImportContestExamDto = z
  .object({
    levelIds: z.string(),
    file: z.instanceof(File),
    contestId: z.number(),
    name: z.string().max(255),
    title: z.string().max(500),
    subjectId: z.number(),
    year: z.number().gte(1800).lte(2025),
  })
  .strict()
  .passthrough();
const ExamImportResultEntity = z
  .object({ slug: z.string().optional(), success: z.boolean() })
  .strict()
  .passthrough();
const ImportUserExamDto = z
  .object({ file: z.instanceof(File), name: z.string().max(255) })
  .strict()
  .passthrough();
const AnyExam: z.ZodType<AnyExam> = z
  .object({
    year: z.number().optional(),
    levels: z.array(Level).optional(),
    levelNames: z.string().optional(),
    id: z.number(),
    name: z.string(),
    title: z.string().optional(),
    slug: z.string(),
    signedUrl: z.string(),
    subjectName: z.string().optional(),
    subjectId: z.number().optional(),
    contestName: z.string().optional(),
    type: z.enum(["exam", "oral-exam"]),
    status: z.object({}).partial().strict().passthrough(),
    isPublic: z.boolean(),
  })
  .strict()
  .passthrough();
const PaginatedAnyExam: z.ZodType<PaginatedAnyExam> = z
  .object({ data: z.array(AnyExam), total: z.number() })
  .strict()
  .passthrough();
const PublicContestExam: z.ZodType<PublicContestExam> = z
  .object({
    year: z.number().optional(),
    levels: z.array(Level),
    levelNames: z.string(),
    id: z.number(),
    name: z.string(),
    title: z.string(),
    slug: z.string(),
    signedUrl: z.string(),
    subjectName: z.string(),
    subjectId: z.number(),
    contestName: z.string().optional(),
    type: z.enum(["exam", "oral-exam"]),
  })
  .strict()
  .passthrough();
const PaginatedPublicContestExam: z.ZodType<PaginatedPublicContestExam> = z
  .object({ data: z.array(PublicContestExam), total: z.number() })
  .strict()
  .passthrough();
const ExamResultForLandingEntity = z
  .object({
    name: z.string(),
    title: z.string(),
    year: z.number(),
    contestName: z.string(),
    slug: z.string(),
  })
  .strict()
  .passthrough();
const ExistingExamsMaxMinYears = z
  .object({ maxYear: z.number(), minYear: z.number() })
  .strict()
  .passthrough();
const UserExam: z.ZodType<UserExam> = z
  .object({
    id: z.number(),
    name: z.string(),
    slug: z.string(),
    signedUrl: z.string(),
  })
  .strict()
  .passthrough();
const PaginatedUserExam: z.ZodType<PaginatedUserExam> = z
  .object({ data: z.array(UserExam), total: z.number() })
  .strict()
  .passthrough();
const ExamStatus = z.enum([
  "SEG_TODO",
  "SEG_PENDING",
  "SEG_DONE",
  "SEG_ERROR",
  "SEG_VERIFIED_PREFIX_TODO",
  "PREFIX_PENDING",
  "PREFIX_DONE",
  "PREFIX_ERROR",
  "PREFIX_VERIFIED_CORRIGE_TODO",
  "CORRIGE_PENDING",
  "CORRIGE_DONE",
  "CORRIGE_ERROR",
  "CORRIGE_VERIFIED_ANSWERS_TODO",
  "ANSWERS_PENDING",
  "ANSWERS_DONE",
  "ANSWERS_ERROR",
  "ANSWERS_VERIFIED_READY",
]);
const ProgrammingLanguage = z.enum(["python", "ocaml", "sql", "c", "scilab"]);
const ExerciseOrExamSectionType = z.enum([
  "question",
  "questionGroup",
  "appendix",
  "other",
  "introduction",
  "header",
  "footer",
  "context",
  "examSubpartTitle",
  "examTitle",
]);
const ExamOrExerciseSectionWithImage: z.ZodType<ExamOrExerciseSectionWithImage> =
  z
    .object({
      type: ExerciseOrExamSectionType,
      language: ProgrammingLanguage.describe(
        "The language of the code"
      ).optional(),
      imageUrl: z.string(),
      answerImageUrl: z.string().optional(),
      isCoding: z.boolean(),
      contentLatex: z.string().optional(),
      answerContentLatex: z.string().optional(),
      indicationContentLatex: z.string().optional(),
      sectionId: z.number(),
      percentage: z.number(),
      questionNumber: z.string().optional(),
      prefix: z.string().optional(),
      subpartTitle: z.string().optional(),
      subpartName: z.string().optional(),
      answerCode: z.string().optional(),
    })
    .strict()
    .passthrough();
const ExamCode: z.ZodType<ExamCode> = z
  .object({
    language: ProgrammingLanguage.describe("The language of the code"),
    code: z.string().describe("The code of the exam or exercise"),
  })
  .strict()
  .passthrough();
const TaskType = z.enum([
  "exam_file_segmentation",
  "exam_solutions_file_segmentation",
  "exam_sections_latex_extraction",
]);
const TaskStatus = z.enum(["in_queue", "running", "success", "error"]);
const Task: z.ZodType<Task> = z
  .object({
    type: TaskType.describe("The type of the task"),
    status: TaskStatus.describe("The status of the task"),
    id: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
    exerciseOrExamId: z.number().optional(),
    userId: z.number(),
    durationMs: z.number().optional(),
    inputTokens: z.number().optional(),
    outputTokens: z.number().optional(),
    cacheTokens: z.number().optional(),
    model: z.string().optional(),
    progress: z.number().optional(),
  })
  .strict()
  .passthrough();
const ExamOrExercise: z.ZodType<ExamOrExercise> = z
  .object({
    status: ExamStatus.describe("The status of the exam or exercise"),
    languages: z
      .array(ProgrammingLanguage)
      .describe("The languages of the exam or exercise")
      .optional(),
    id: z.number(),
    name: z.string().optional(),
    title: z.string().optional(),
    type: z.enum(["exercise", "exam", "oral-exam"]),
    subjectId: z.number().optional(),
    subjectName: z.string().optional(),
    slug: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    documentUrlAsImage: z.string(),
    contestId: z.number().optional(),
    contestName: z.string().optional(),
    year: z.number().optional(),
    durationInHours: z.string().optional(),
    sections: z.array(ExamOrExerciseSectionWithImage),
    levels: z.array(Level),
    levelNames: z.string(),
    canEdit: z.boolean(),
    canDelete: z.boolean(),
    isPublic: z.boolean(),
    codes: z.array(ExamCode).optional(),
    tasks: z.array(Task).optional(),
    isSegmentingWithAiOrInQueue: z.boolean(),
    isSegmentingSolutionsFileWithAiOrInQueue: z.boolean(),
    isExtractingSectionsLatexWithAiOrInQueue: z.boolean(),
  })
  .strict()
  .passthrough();
const ExamOrExerciseSectionFull: z.ZodType<ExamOrExerciseSectionFull> = z
  .object({
    type: ExerciseOrExamSectionType,
    language: ProgrammingLanguage.describe(
      "The language of the code"
    ).optional(),
    answerImageUrl: z.string().optional(),
    solutionSectionContentLatex: z.string().optional(),
    solutionSectionCodeAnswer: z.string().optional(),
    solutionsSectionId: z.number().optional(),
    solutionSectionContainsSchemaOrDrawing: z.boolean().optional(),
    solutionSectionSchemaDescription: z.string().optional(),
    containsSchemaOrDrawing: z.boolean(),
    schemaDescription: z.string().optional(),
    subpartTitle: z.string().optional(),
    isCoding: z.boolean(),
    contentLatex: z.string().optional(),
    answerContentLatex: z.string().optional(),
    indicationContentLatex: z.string().optional(),
    sectionId: z.number(),
    percentage: z.number(),
    imageUrl: z.string().optional(),
    questionNumber: z.string().optional(),
    prefix: z.string().optional(),
    subpartName: z.string().optional(),
    answerCode: z.string().optional(),
  })
  .strict()
  .passthrough();
const ProgrammingLanguageForUpdate = z.enum([
  "python",
  "ocaml",
  "sql",
  "c",
  "scilab",
  "no-code",
]);
const UpdateSectionDto: z.ZodType<UpdateSectionDto> = z
  .object({
    type: ExerciseOrExamSectionType.describe(
      "The type of the section of the exam or exercise"
    ),
    language: ProgrammingLanguageForUpdate.describe(
      "The language of the code (must be no-code if no language)"
    ),
    answerContentLatex: z.string(),
    contentLatex: z.string(),
    containsSchemaOrDrawing: z.boolean(),
    schemaDescription: z.string(),
    subpartTitle: z.string(),
    subpartName: z.string(),
    indicationContentLatex: z.string(),
    answerCode: z.string(),
    questionNumber: z.string(),
    prefix: z.string(),
  })
  .partial()
  .strict()
  .passthrough();
const ExerciseOrExamSolutionsFileSectionType = z.enum(["solution", "other"]);
const SolutionsFileSection: z.ZodType<SolutionsFileSection> = z
  .object({
    type: ExerciseOrExamSolutionsFileSectionType,
    percentage: z.number(),
    id: z.number(),
    sectionId: z.number().optional(),
  })
  .strict()
  .passthrough();
const HasSolutionsFileResult: z.ZodType<HasSolutionsFileResult> = z
  .object({
    hasSolutionsFile: z.boolean(),
    solutionsFilePagesAsImages: z.array(z.string()).optional(),
    solutionsFileSections: z.array(SolutionsFileSection).optional(),
  })
  .strict()
  .passthrough();
const ExerciseOrExamType = z.enum(["exercise", "exam", "oral-exam"]);
const ExamOrExerciseSectionForUpdateMode = z.enum([
  "is_added",
  "is_updated",
  "is_deleted",
]);
const ExamOrExerciseSectionForUpdate: z.ZodType<ExamOrExerciseSectionForUpdate> =
  z
    .object({
      type: ExerciseOrExamSectionType,
      language: ProgrammingLanguageForUpdate.describe(
        "The language of the code (must be no-code if no language)"
      ).optional(),
      percentage: z
        .number()
        .gte(0)
        .lte(100)
        .describe("Percentage of where the section ends in the exam"),
      mode: ExamOrExerciseSectionForUpdateMode.optional(),
      contentLatex: z.string().optional(),
      answerContentLatex: z.string().optional(),
      indicationContentLatex: z.string().optional(),
      questionNumber: z.string().optional(),
      prefix: z.string().optional(),
      subpartTitle: z.string().optional(),
      subpartName: z.string().optional(),
      answerCode: z.string().optional(),
      sectionId: z.number().gte(1).optional(),
    })
    .strict()
    .passthrough();
const UpdateExamOrExerciseDto: z.ZodType<UpdateExamOrExerciseDto> = z
  .object({
    status: ExamStatus,
    type: ExerciseOrExamType,
    title: z.string(),
    name: z.string(),
    year: z.number().lte(2025),
    durationInHours: z.string(),
    annotatorComment: z.string(),
    contestId: z.number(),
    slug: z.string(),
    sqlQuery: z.string(),
    sections: z.array(ExamOrExerciseSectionForUpdate).min(1),
    levelIds: z.array(z.number()),
    codes: z.array(ExamCode),
  })
  .partial()
  .strict()
  .passthrough();
const UpdateExamOrExerciseSolutionsFileDto = z
  .object({ file: z.instanceof(File) })
  .strict()
  .passthrough();
const SolutionsFileSectionForUpdateMode = z.enum([
  "is_added",
  "is_updated",
  "is_deleted",
]);
const SolutionsFileSectionForUpdate: z.ZodType<SolutionsFileSectionForUpdate> =
  z
    .object({
      type: ExerciseOrExamSolutionsFileSectionType,
      mode: SolutionsFileSectionForUpdateMode.optional(),
      id: z.number().gte(1).optional(),
      percentage: z.number(),
      sectionId: z.number().optional(),
    })
    .strict()
    .passthrough();
const ExamOrExerciseSolutionsFileSectionsDto: z.ZodType<ExamOrExerciseSolutionsFileSectionsDto> =
  z
    .object({ sections: z.array(SolutionsFileSectionForUpdate) })
    .strict()
    .passthrough();
const DownloadableLink = z.object({ url: z.string() }).strict().passthrough();
const UpdateExamOrExerciseFileDto = z
  .object({ file: z.instanceof(File) })
  .strict()
  .passthrough();
const RequestAiSectionHelpDto = z
  .object({ userAnswer: z.string(), userAnswerCode: z.string() })
  .partial()
  .strict()
  .passthrough();
const AiIndication = z
  .object({ indication: z.string() })
  .strict()
  .passthrough();
const AiCorrection = z
  .object({ correction: z.string() })
  .strict()
  .passthrough();
const CreateModelDto = z
  .object({
    name: z.string(),
    model: z.string(),
    providerName: z.string(),
    contextLength: z.number(),
    maxOutputTokens: z.number(),
    maxInputTokens: z.number(),
    supportsImageInput: z.boolean(),
    supportsAudioInput: z.boolean(),
    supportsJsonOutput: z.boolean(),
  })
  .strict()
  .passthrough();
const CreateSchoolDto = z
  .object({
    name: z.string().min(3).describe("The name of the school"),
    nationalId: z
      .string()
      .describe("The national ID of the school (optional)")
      .optional(),
    city: z
      .string()
      .describe("The city where the school is located (optional)")
      .optional(),
  })
  .strict()
  .passthrough();
const SchoolItem = z
  .object({
    id: z.number(),
    name: z.string(),
    city: z.string().optional(),
    zipCode: z.string().optional(),
  })
  .strict()
  .passthrough();
const TargetSchoolGroupsEntity: z.ZodType<TargetSchoolGroupsEntity> = z
  .object({ targetSchoolGroups: z.array(TargetSchoolGroup) })
  .strict()
  .passthrough();
const School: z.ZodType<School> = z
  .object({
    name: z.string(),
    id: z.number(),
    levels: z.array(Level),
    nationalId: z.string().optional(),
    city: z.string().optional(),
    zipCode: z.string().optional(),
  })
  .strict()
  .passthrough();
const UpdateSchoolDto = z
  .object({
    name: z.string().min(3).describe("The name of the school"),
    nationalId: z.string().describe("The national ID of the school"),
    city: z.string().describe("The city where the school is located"),
  })
  .partial()
  .strict()
  .passthrough();
const CreateStudentGroupDto = z
  .object({
    name: z.string().min(3),
    studentUsers: z.array(z.string().min(2)).min(1),
  })
  .strict()
  .passthrough();
const Student: z.ZodType<Student> = z
  .object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    username: z.string(),
  })
  .strict()
  .passthrough();
const StudentGroup: z.ZodType<StudentGroup> = z
  .object({
    userId: z.number(),
    students: z.array(Student),
    name: z.string(),
    id: z.number(),
  })
  .strict()
  .passthrough();
const UpdateStudentGroupDto = z
  .object({ name: z.string().min(3), studentUsernames: z.array(z.string()) })
  .partial()
  .strict()
  .passthrough();
const CreateStudentExamSharingDto = z
  .object({ examId: z.number(), groupIds: z.array(z.number()) })
  .strict()
  .passthrough();
const UpdateStudentExamSharingDto = z
  .object({ examId: z.number(), groupIds: z.array(z.number()) })
  .strict()
  .passthrough();
const StudentGroupWithNoDetails: z.ZodType<StudentGroupWithNoDetails> = z
  .object({ name: z.string(), id: z.number() })
  .strict()
  .passthrough();
const ExamSharing: z.ZodType<ExamSharing> = z
  .object({
    examId: z.number(),
    examName: z.string(),
    examSlug: z.string(),
    studentGroups: z.array(StudentGroupWithNoDetails),
  })
  .strict()
  .passthrough();
const CreateOrUpdateProfessorCommentDto = z
  .object({ comment: z.string(), answerId: z.number() })
  .strict()
  .passthrough();
const StudentAnswerForSharedExamSection = z
  .object({
    answerId: z.number().optional(),
    answer: z.string().optional(),
    codeAnswer: z.string().optional(),
    professorComment: z.string().optional(),
    studentName: z.string(),
    studentId: z.number(),
  })
  .strict()
  .passthrough();
const ExamSharedToMe = z
  .object({
    examId: z.number(),
    examName: z.string(),
    examSlug: z.string(),
    sharedByFullName: z.string(),
  })
  .strict()
  .passthrough();
const CreateOrUpdateStudentSectionAnswerDto = z
  .object({ sectionId: z.number(), answer: z.string(), codeAnswer: z.string() })
  .strict()
  .passthrough();
const Answer: z.ZodType<Answer> = z
  .object({ answerId: z.number(), answer: z.string(), sectionId: z.number() })
  .strict()
  .passthrough();
const StudentProgressForExam: z.ZodType<StudentProgressForExam> = z
  .object({
    userId: z.number(),
    firstName: z.string(),
    lastName: z.string().optional(),
    answers: z.array(Answer),
  })
  .strict()
  .passthrough();
const StudentAnswerForSection = z
  .object({
    answer: z.string(),
    codeAnswer: z.string(),
    professorComment: z.string(),
  })
  .strict()
  .passthrough();
const ExecuteCodeDto = z
  .object({ sourceCode: z.string() })
  .strict()
  .passthrough();
const CodeSubmissionCreationEntity = z
  .object({ submissionId: z.number() })
  .strict()
  .passthrough();
const CodeSubmissionStatus = z.enum(["pending", "success", "error"]);
const CodeSubmissionResultEntity: z.ZodType<CodeSubmissionResultEntity> = z
  .object({
    status: CodeSubmissionStatus,
    id: z.number(),
    sectionId: z.number(),
    stdOut: z.string(),
    stdErr: z.string(),
  })
  .strict()
  .passthrough();
const SendMessageDto = z
  .object({
    firstName: z.string(),
    surname: z.string(),
    email: z.string(),
    message: z.string(),
    captchaToken: z.string(),
  })
  .strict()
  .passthrough();
const AppUser = z
  .object({
    id: z.number(),
    email: z.string(),
    username: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    role: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    levelName: z.string(),
    schoolName: z.string(),
    targetSchoolGroup: z.string().optional(),
    targetContests: z.string().optional(),
    phone: z.string(),
    emailValidated: z.boolean(),
  })
  .strict()
  .passthrough();
const SendEmailToSelectedUsersDto = z
  .object({
    userIds: z.array(z.number()),
    subject: z.string(),
    message: z.string(),
  })
  .strict()
  .passthrough();
const CreateNewConversationDto = z
  .object({ initialMessage: z.string() })
  .strict()
  .passthrough();
const UserConversation = z
  .object({ id: z.string(), title: z.string(), createdAt: z.string() })
  .strict()
  .passthrough();
const UserConversationMessageRole = z.enum(["assistant", "user"]);
const ConversationMessage: z.ZodType<ConversationMessage> = z
  .object({
    role: UserConversationMessageRole.describe(
      "The role of the user conversation message"
    ),
    id: z.string(),
    content: z.string(),
    createdAt: z.string(),
  })
  .strict()
  .passthrough();
const FullUserConversation: z.ZodType<FullUserConversation> = z
  .object({
    id: z.string(),
    title: z.string(),
    createdAt: z.string(),
    messages: z.array(ConversationMessage),
  })
  .strict()
  .passthrough();
const AiInterviewTemplate = z
  .object({
    id: z.number(),
    name: z.string(),
    description: z.string().optional(),
    price: z.number(),
    productId: z.number(),
  })
  .strict()
  .passthrough();
const AiInterviewStatus = z.enum([
  "pending",
  "in_progress",
  "completed",
  "error",
]);
const AiInterviewMessage: z.ZodType<AiInterviewMessage> = z
  .object({
    user: z
      .enum(["bot", "interviewee"])
      .describe("The user who sent the message"),
    message: z.string(),
    createdAt: z.string(),
  })
  .strict()
  .passthrough();
const AiInterview: z.ZodType<AiInterview> = z
  .object({
    status: AiInterviewStatus.describe("The status of an ai interview"),
    id: z.number(),
    name: z.string(),
    description: z.string().optional(),
    price: z.number(),
    dailyRoomUrl: z.string().optional(),
    aiInterviewTemplateId: z.number(),
    transcription: z.array(AiInterviewMessage).optional(),
    evaluation: z.string().optional(),
    startedAt: z.string().optional(),
  })
  .strict()
  .passthrough();
const AiInterviewWithDetails: z.ZodType<AiInterviewWithDetails> = z
  .object({
    status: AiInterviewStatus.describe("The status of an ai interview"),
    id: z.number(),
    name: z.string(),
    description: z.string().optional(),
    price: z.number(),
    dailyRoomUrl: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    startedAt: z.string().optional(),
    finishedAt: z.string().optional(),
    pid: z.string().optional(),
    logPath: z.string().optional(),
    pythonScript: z.string().optional(),
    envVariables: z.object({}).partial().strict().passthrough().optional(),
    logs: z.string().optional(),
    userId: z.number(),
    userName: z.string(),
    userOrderItemId: z.number().optional(),
    productId: z.number().optional(),
    aiInterviewTemplateId: z.number(),
    currency: z.string(),
    transcription: z.array(AiInterviewMessage).optional(),
    evaluation: z.string().optional(),
    evaluationInstructions: z.string().optional(),
  })
  .strict()
  .passthrough();
const PaginatedAiInterviewWithDetails: z.ZodType<PaginatedAiInterviewWithDetails> =
  z
    .object({ data: z.array(AiInterviewWithDetails), total: z.number() })
    .strict()
    .passthrough();
const StartAdminAiInterviewForTestingDto = z
  .object({
    aiInterviewTemplateId: z
      .number()
      .describe("The ID of the AI interview template to use"),
  })
  .strict()
  .passthrough();
const AdminAiInterviewForTesting = z
  .object({ id: z.number(), dailyRoomUrl: z.string() })
  .strict()
  .passthrough();
const CreateAiInterviewTemplateDto = z
  .object({
    name: z.string(),
    description: z.string(),
    evaluationInstructions: z.string().optional(),
    introductionPrompt: z.string().optional(),
    productPrice: z.string(),
  })
  .strict()
  .passthrough();
const AiInterviewTemplateWithDetails = z
  .object({
    id: z.number(),
    name: z.string(),
    description: z.string().optional(),
    evaluationInstructions: z.string().optional(),
    introductionPrompt: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    productId: z.number(),
    productName: z.string(),
    productPrice: z.string(),
    productCurrency: z.string(),
    isActive: z.boolean(),
  })
  .strict()
  .passthrough();
const UpdateAiInterviewTemplateDto = z
  .object({
    name: z.string(),
    description: z.string(),
    evaluationInstructions: z.string().optional(),
    introductionPrompt: z.string().optional(),
    productPrice: z.string(),
    isActive: z.boolean().optional(),
  })
  .strict()
  .passthrough();
const UserOrderStatus = z.enum([
  "processing_payment",
  "payment_failed",
  "payment_succeeded",
  "refunded",
  "checkout_session_creation_error",
]);
const UserOrderItem: z.ZodType<UserOrderItem> = z
  .object({
    productId: z.number(),
    quantity: z.number(),
    aiInterviewTemplateId: z.number().optional(),
    productName: z.string(),
    unitPrice: z.number(),
    description: z.string().optional(),
  })
  .strict()
  .passthrough();
const UserOrder: z.ZodType<UserOrder> = z
  .object({
    status: UserOrderStatus.describe("The status of the user order"),
    id: z.number(),
    reference: z.string(),
    items: z.array(UserOrderItem),
    clientSecret: z.string().optional(),
  })
  .strict()
  .passthrough();
const CartItemDto: z.ZodType<CartItemDto> = z
  .object({
    aiInterviewTemplateId: z.number().optional(),
    productId: z.number(),
    quantity: z.number().gte(1),
  })
  .strict()
  .passthrough();
const UpdateUserCartDto: z.ZodType<UpdateUserCartDto> = z
  .object({ items: z.array(CartItemDto) })
  .strict()
  .passthrough();
const CartItem = z
  .object({
    id: z.number(),
    aiInterviewTemplateId: z.number().optional(),
    productId: z.number(),
    productName: z.string(),
    unitPrice: z.number(),
    quantity: z.number(),
  })
  .strict()
  .passthrough();
const FindAllExamsLimit = z.enum(["10", "20", "50", "100"]);
const FindAllExamsType = z.enum(["exam", "oral-exam"]);

export const schemas = {
  TargetSchoolGroup,
  Gender,
  UserProfileInfo,
  Username,
  TargetSchoolGroupEnum,
  GenderEnum,
  UpdateProfileDto,
  SignUpDto,
  SignInDto,
  SignInResponseDto,
  VerifyEmailDto,
  SendVerificationEmailResponse,
  CheckEmailAvailableDto,
  CheckEmailResponseDto,
  CheckUsernameAvailableDto,
  CheckUsernameResponseDto,
  AskResetPasswordDto,
  ResetPasswordDto,
  UpdateEmailDto,
  UpdatePasswordDto,
  AuthenticationStatusEntity,
  CreateCourseDto,
  CreateCourseResponseDto,
  Course,
  UpdateCourseDto,
  CreateLevelDto,
  Level,
  LevelGroup,
  CreateLevelGroupDto,
  UpdateLevelGroupDto,
  UpdateLevelDto,
  MergeLevelsDto,
  CreateContestDto,
  Contest,
  UpdateContestDto,
  MergeContestsDto,
  Subject,
  CreateChapterDto,
  ShortCourse,
  Chapter,
  UpdateChapterDto,
  ImportContestExamDto,
  ExamImportResultEntity,
  ImportUserExamDto,
  AnyExam,
  PaginatedAnyExam,
  PublicContestExam,
  PaginatedPublicContestExam,
  ExamResultForLandingEntity,
  ExistingExamsMaxMinYears,
  UserExam,
  PaginatedUserExam,
  ExamStatus,
  ProgrammingLanguage,
  ExerciseOrExamSectionType,
  ExamOrExerciseSectionWithImage,
  ExamCode,
  TaskType,
  TaskStatus,
  Task,
  ExamOrExercise,
  ExamOrExerciseSectionFull,
  ProgrammingLanguageForUpdate,
  UpdateSectionDto,
  ExerciseOrExamSolutionsFileSectionType,
  SolutionsFileSection,
  HasSolutionsFileResult,
  ExerciseOrExamType,
  ExamOrExerciseSectionForUpdateMode,
  ExamOrExerciseSectionForUpdate,
  UpdateExamOrExerciseDto,
  UpdateExamOrExerciseSolutionsFileDto,
  SolutionsFileSectionForUpdateMode,
  SolutionsFileSectionForUpdate,
  ExamOrExerciseSolutionsFileSectionsDto,
  DownloadableLink,
  UpdateExamOrExerciseFileDto,
  RequestAiSectionHelpDto,
  AiIndication,
  AiCorrection,
  CreateModelDto,
  CreateSchoolDto,
  SchoolItem,
  TargetSchoolGroupsEntity,
  School,
  UpdateSchoolDto,
  CreateStudentGroupDto,
  Student,
  StudentGroup,
  UpdateStudentGroupDto,
  CreateStudentExamSharingDto,
  UpdateStudentExamSharingDto,
  StudentGroupWithNoDetails,
  ExamSharing,
  CreateOrUpdateProfessorCommentDto,
  StudentAnswerForSharedExamSection,
  ExamSharedToMe,
  CreateOrUpdateStudentSectionAnswerDto,
  Answer,
  StudentProgressForExam,
  StudentAnswerForSection,
  ExecuteCodeDto,
  CodeSubmissionCreationEntity,
  CodeSubmissionStatus,
  CodeSubmissionResultEntity,
  SendMessageDto,
  AppUser,
  SendEmailToSelectedUsersDto,
  CreateNewConversationDto,
  UserConversation,
  UserConversationMessageRole,
  ConversationMessage,
  FullUserConversation,
  AiInterviewTemplate,
  AiInterviewStatus,
  AiInterviewMessage,
  AiInterview,
  AiInterviewWithDetails,
  PaginatedAiInterviewWithDetails,
  StartAdminAiInterviewForTestingDto,
  AdminAiInterviewForTesting,
  CreateAiInterviewTemplateDto,
  AiInterviewTemplateWithDetails,
  UpdateAiInterviewTemplateDto,
  UserOrderStatus,
  UserOrderItem,
  UserOrder,
  CartItemDto,
  UpdateUserCartDto,
  CartItem,
  FindAllExamsLimit,
  FindAllExamsType,
};

const endpoints = makeApi([
  {
    method: "post",
    path: "/admin/send-email-to-selected-users",
    alias: "sendEmailToSelectedUsers",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SendEmailToSelectedUsersDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/admin/users",
    alias: "getUsers",
    requestFormat: "json",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(AppUser),
  },
  {
    method: "get",
    path: "/ai-interviews",
    alias: "getActiveAiInterviewTemplates",
    requestFormat: "json",
    response: z.array(AiInterviewTemplate),
  },
  {
    method: "get",
    path: "/ai-interviews/admin/ai-interview-templates",
    alias: "getAiInterviewTemplatesWithDetails",
    requestFormat: "json",
    response: z.array(AiInterviewTemplateWithDetails),
  },
  {
    method: "get",
    path: "/ai-interviews/admin/all",
    alias: "getAdminAiInterviews",
    requestFormat: "json",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "page",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: PaginatedAiInterviewWithDetails,
  },
  {
    method: "post",
    path: "/ai-interviews/admin/create-ai-interview-template",
    alias: "createAiInterviewTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateAiInterviewTemplateDto,
      },
    ],
    response: AiInterviewTemplateWithDetails,
  },
  {
    method: "post",
    path: "/ai-interviews/admin/generate-transcription-evaluation/:aiInterviewId",
    alias: "generateTranscriptionAndEvaluation",
    requestFormat: "json",
    parameters: [
      {
        name: "aiInterviewId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/ai-interviews/admin/start-ai-interview-for-testing",
    alias: "startAdminAiInterviewForTesting",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({
            aiInterviewTemplateId: z
              .number()
              .describe("The ID of the AI interview template to use"),
          })
          .strict()
          .passthrough(),
      },
    ],
    response: AdminAiInterviewForTesting,
  },
  {
    method: "put",
    path: "/ai-interviews/admin/update-ai-interview-template/:id",
    alias: "updateAiInterviewTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateAiInterviewTemplateDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: AiInterviewTemplateWithDetails,
  },
  {
    method: "get",
    path: "/ai-interviews/ai-interview/:aiInterviewId",
    alias: "getAiInterview",
    requestFormat: "json",
    parameters: [
      {
        name: "aiInterviewId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: AiInterview,
  },
  {
    method: "get",
    path: "/ai-interviews/my-ai-interviews",
    alias: "getMyAiInterviews",
    requestFormat: "json",
    parameters: [
      {
        name: "status",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(AiInterview),
  },
  {
    method: "post",
    path: "/ai-interviews/start-ai-interview/:aiInterviewId",
    alias: "startAiInterview",
    requestFormat: "json",
    parameters: [
      {
        name: "aiInterviewId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: AiInterview,
  },
  {
    method: "post",
    path: "/authentication/ask-reset-password",
    alias: "askResetPassword",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ email: z.string().email() }).strict().passthrough(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/authentication/ask-update-email",
    alias: "askUpdateEmail",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({ newEmail: z.string().email() })
          .strict()
          .passthrough(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/authentication/check-email-available",
    alias: "checkEmailAvailable",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ email: z.string().email() }).strict().passthrough(),
      },
    ],
    response: z.object({ emailExists: z.boolean() }).strict().passthrough(),
  },
  {
    method: "post",
    path: "/authentication/check-username-available",
    alias: "checkUsernameAvailable",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ username: z.string() }).strict().passthrough(),
      },
    ],
    response: z.object({ usernameExists: z.boolean() }).strict().passthrough(),
  },
  {
    method: "get",
    path: "/authentication/is-authenticated",
    alias: "isAuthenticated",
    requestFormat: "json",
    response: AuthenticationStatusEntity,
  },
  {
    method: "post",
    path: "/authentication/resend-verification-email",
    alias: "resendVerificationEmail",
    requestFormat: "json",
    response: SendVerificationEmailResponse,
    errors: [
      {
        status: 400,
        description: `Return a bad request error if email has already been validated.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/authentication/reset-password",
    alias: "resetPassword",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ResetPasswordDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/authentication/sign-in",
    alias: "signIn",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SignInDto,
      },
    ],
    response: SignInResponseDto,
  },
  {
    method: "post",
    path: "/authentication/sign-out",
    alias: "signOut",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/authentication/sign-up",
    alias: "signUp",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SignUpDto,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Return a bad request error if email is already registered or username is already taken.`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/authentication/update-email",
    alias: "updateEmail",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ token: z.string() }).strict().passthrough(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/authentication/update-password",
    alias: "updatePassword",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdatePasswordDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/authentication/verify-email",
    alias: "verifyEmail",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ token: z.string() }).strict().passthrough(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/chapter",
    alias: "createChapter",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateChapterDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/chapter",
    alias: "findAllChapters",
    requestFormat: "json",
    parameters: [
      {
        name: "levelId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "subjectId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(Chapter),
  },
  {
    method: "patch",
    path: "/chapter/:id",
    alias: "updateChapter",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateChapterDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/chapter/:id",
    alias: "removeChapter",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/chapter/:slug",
    alias: "findOneChapter",
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: Chapter,
  },
  {
    method: "post",
    path: "/contact",
    alias: "contact",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SendMessageDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/contest",
    alias: "createContest",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({ name: z.string().min(3) })
          .strict()
          .passthrough(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/contest",
    alias: "findAllContests",
    requestFormat: "json",
    parameters: [
      {
        name: "mustHaveExams",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "levelId",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(Contest),
  },
  {
    method: "get",
    path: "/contest/:id",
    alias: "findOneContest",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: Contest,
  },
  {
    method: "put",
    path: "/contest/:id",
    alias: "updateContest",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({ name: z.string().min(3) })
          .partial()
          .strict()
          .passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/contest/:id",
    alias: "removeContest",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/contest/merge",
    alias: "mergeContests",
    description: `Merge two contests into one`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MergeContestsDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/conversation",
    alias: "createNewConversation",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ initialMessage: z.string() }).strict().passthrough(),
      },
    ],
    response: UserConversation,
  },
  {
    method: "get",
    path: "/conversation",
    alias: "getConversations",
    requestFormat: "json",
    response: z.array(UserConversation),
  },
  {
    method: "get",
    path: "/conversation/:id",
    alias: "getConversation",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: FullUserConversation,
  },
  {
    method: "post",
    path: "/course",
    alias: "create",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateCourseDto,
      },
    ],
    response: z.object({ courseId: z.number() }).strict().passthrough(),
    errors: [
      {
        status: 400,
        description: `Video file does not exist`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/course",
    alias: "findAll",
    requestFormat: "json",
    parameters: [
      {
        name: "levelId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "subjectId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(Course),
  },
  {
    method: "get",
    path: "/course/:id",
    alias: "findOne",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: Course,
  },
  {
    method: "patch",
    path: "/course/:id",
    alias: "update",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateCourseDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/course/:id",
    alias: "remove",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/exam-or-exercise/:id",
    alias: "updateExamOrExercise",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateExamOrExerciseDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/exam-or-exercise/:id",
    alias: "removeExamOrExercise",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/exam-or-exercise/:slug",
    alias: "findOneExamOrExercise",
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ExamOrExercise,
    errors: [
      {
        status: 404,
        description: `Exam or exercise with given slug not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/exam-or-exercise/ask-for-solutions-file-segmentation/:examOrExerciseId",
    alias: "askForSolutionsFileSegmentation",
    requestFormat: "json",
    parameters: [
      {
        name: "examOrExerciseId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/exam-or-exercise/download-pdf/:slug",
    alias: "getExamDownloadablePdf",
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ url: z.string() }).strict().passthrough(),
  },
  {
    method: "patch",
    path: "/exam-or-exercise/exam-file/:id",
    alias: "updateExamOrExerciseFile",
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({ file: z.instanceof(File) })
          .strict()
          .passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/exam-or-exercise/exams/all",
    alias: "findAllExams",
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.enum(["10", "20", "50", "100"]).optional(),
      },
      {
        name: "type",
        type: "Query",
        schema: z
          .enum(["exam", "oral-exam"])
          .describe("The type of the exams")
          .optional(),
      },
      {
        name: "levelId",
        type: "Query",
        schema: z.string().describe("The level ids of the exams").optional(),
      },
      {
        name: "contestId",
        type: "Query",
        schema: z.string().describe("The contest ids of the exams").optional(),
      },
      {
        name: "subjectId",
        type: "Query",
        schema: z.string().describe("The subject ids of the exams").optional(),
      },
      {
        name: "year",
        type: "Query",
        schema: z.string().describe("The years of the exams").optional(),
      },
      {
        name: "status",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "isPublic",
        type: "Query",
        schema: z.string().describe("Filter on public exams").optional(),
      },
      {
        name: "page",
        type: "Query",
        schema: z.number().lte(1000).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedAnyExam,
  },
  {
    method: "get",
    path: "/exam-or-exercise/exams/landing",
    alias: "findAllExamsForLanding",
    requestFormat: "json",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(ExamResultForLandingEntity),
  },
  {
    method: "get",
    path: "/exam-or-exercise/exams/max-and-min-years",
    alias: "findMaxAndMinYears",
    requestFormat: "json",
    parameters: [
      {
        name: "mustHaveExistingExams",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: ExistingExamsMaxMinYears,
  },
  {
    method: "get",
    path: "/exam-or-exercise/exams/my-exams",
    alias: "findAllUserExams",
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.enum(["10", "20", "50", "100"]).optional(),
      },
      {
        name: "type",
        type: "Query",
        schema: z
          .enum(["exam", "oral-exam"])
          .describe("The type of the exams")
          .optional(),
      },
      {
        name: "levelId",
        type: "Query",
        schema: z.string().describe("The level ids of the exams").optional(),
      },
      {
        name: "contestId",
        type: "Query",
        schema: z.string().describe("The contest ids of the exams").optional(),
      },
      {
        name: "subjectId",
        type: "Query",
        schema: z.string().describe("The subject ids of the exams").optional(),
      },
      {
        name: "year",
        type: "Query",
        schema: z.string().describe("The years of the exams").optional(),
      },
      {
        name: "page",
        type: "Query",
        schema: z.number().lte(1000).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedUserExam,
  },
  {
    method: "get",
    path: "/exam-or-exercise/exams/public-contests",
    alias: "findAllPublicContestExams",
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.enum(["10", "20", "50", "100"]).optional(),
      },
      {
        name: "type",
        type: "Query",
        schema: z
          .enum(["exam", "oral-exam"])
          .describe("The type of the exams")
          .optional(),
      },
      {
        name: "levelId",
        type: "Query",
        schema: z.string().describe("The level ids of the exams").optional(),
      },
      {
        name: "contestId",
        type: "Query",
        schema: z.string().describe("The contest ids of the exams").optional(),
      },
      {
        name: "subjectId",
        type: "Query",
        schema: z.string().describe("The subject ids of the exams").optional(),
      },
      {
        name: "year",
        type: "Query",
        schema: z.string().describe("The years of the exams").optional(),
      },
      {
        name: "page",
        type: "Query",
        schema: z.number().lte(1000).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PaginatedPublicContestExam,
  },
  {
    method: "post",
    path: "/exam-or-exercise/extract-and-reformulate-section-answer/:sectionId",
    alias: "extractAndReformulateSectionAnswer",
    requestFormat: "json",
    parameters: [
      {
        name: "sectionId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.string(),
  },
  {
    method: "post",
    path: "/exam-or-exercise/extract-exam-sections-latex-and-reformulate-answers/:examId",
    alias: "extractExamSectionsLatexAndReformulateAnswers",
    requestFormat: "json",
    parameters: [
      {
        name: "examId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/exam-or-exercise/has-solutions-file/:examOrExerciseId",
    alias: "hasSolutionsFile",
    requestFormat: "json",
    parameters: [
      {
        name: "examOrExerciseId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: HasSolutionsFileResult,
  },
  {
    method: "post",
    path: "/exam-or-exercise/import-contest-exam",
    alias: "importContestExam",
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ImportContestExamDto,
      },
    ],
    response: ExamImportResultEntity,
  },
  {
    method: "post",
    path: "/exam-or-exercise/import-user-exam",
    alias: "importUserExam",
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ImportUserExamDto,
      },
    ],
    response: ExamImportResultEntity,
  },
  {
    method: "post",
    path: "/exam-or-exercise/request-ai-correction/:sectionId",
    alias: "requestAiCorrection",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RequestAiSectionHelpDto,
      },
      {
        name: "sectionId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.object({ correction: z.string() }).strict().passthrough(),
  },
  {
    method: "post",
    path: "/exam-or-exercise/request-ai-indication/:sectionId",
    alias: "requestAiIndication",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RequestAiSectionHelpDto,
      },
      {
        name: "sectionId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.object({ indication: z.string() }).strict().passthrough(),
  },
  {
    method: "get",
    path: "/exam-or-exercise/sections/:sectionId",
    alias: "findSection",
    requestFormat: "json",
    parameters: [
      {
        name: "sectionId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: ExamOrExerciseSectionFull,
  },
  {
    method: "patch",
    path: "/exam-or-exercise/sections/:sectionId",
    alias: "updateSection",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateSectionDto,
      },
      {
        name: "sectionId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/exam-or-exercise/segment-with-ai/:examOrExerciseId",
    alias: "segmentWithAi",
    requestFormat: "json",
    parameters: [
      {
        name: "examOrExerciseId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/exam-or-exercise/solutions-file-sections/:id",
    alias: "updateExamOrExerciseSolutionsFileSections",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ExamOrExerciseSolutionsFileSectionsDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/exam-or-exercise/solutions-file/:id",
    alias: "addExamOrExerciseSolutionsFile",
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({ file: z.instanceof(File) })
          .strict()
          .passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/exam-or-exercise/solutions-file/:id",
    alias: "removeExamOrExerciseSolutionsFile",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/exam-or-exercise/test-ai",
    alias: "testAi",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "delete",
    path: "/exam-or-exercise/user-exams/:id",
    alias: "removeUserExamOrExercise",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/level",
    alias: "createLevel",
    description: `Create a new level with the given name and optional level group ID`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateLevelDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/level",
    alias: "findAllLevels",
    description: `Get all levels in a single array. A level is a specific program a student is enrolled in. For example, &quot;MPSI&quot;, &quot;Licence de Maths Premiere année&quot;, &quot;Doctorat Informatique&quot;, etc.`,
    requestFormat: "json",
    parameters: [
      {
        name: "mustHaveExams",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "levelGroupId",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(Level),
  },
  {
    method: "put",
    path: "/level/:id",
    alias: "updateLevel",
    description: `Update a level by its ID`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateLevelDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/level/:id",
    alias: "deleteLevel",
    description: `Delete a level by its ID`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/level/level-groups",
    alias: "findAllLevelGroups",
    description: `Get all level groups. A level group is a group of levels. For example, &quot;Prepa&quot;, &quot;Licence&quot;, &quot;Ecole d&#x27;ingenieur&quot;, etc. For each level group, its related levels are also returned.`,
    requestFormat: "json",
    response: z.array(LevelGroup),
  },
  {
    method: "post",
    path: "/level/level-groups",
    alias: "createLevelGroup",
    description: `Create a level group.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateLevelGroupDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/level/level-groups/:id",
    alias: "updateLevelGroup",
    description: `Update a level group.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateLevelGroupDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/level/merge",
    alias: "mergeLevels",
    description: `Merge two levels into one`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: MergeLevelsDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/llm/create-model",
    alias: "createModel",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateModelDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/llm/models",
    alias: "getModels",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/llm/models-providers",
    alias: "getModelsProviders",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/school",
    alias: "createSchool",
    description: `Create a new school with the given name and optional fields`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSchoolDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/school",
    alias: "findAllSchools",
    description: `Get all schools. A school is a specific institution where students are enrolled. For example, &quot;Lycée Louis-le-Grand&quot;, &quot;Université Paris-Saclay&quot;, &quot;Ecole 42&quot;, etc.`,
    requestFormat: "json",
    parameters: [
      {
        name: "text",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "levelId",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(SchoolItem),
  },
  {
    method: "get",
    path: "/school/:id",
    alias: "findSchool",
    description: `Get a school by its ID`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: School,
  },
  {
    method: "put",
    path: "/school/:id",
    alias: "updateSchool",
    description: `Update a school by its ID`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateSchoolDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/school/:id",
    alias: "deleteSchool",
    description: `Delete a school by its ID`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/school/:id/link-level/:levelId",
    alias: "createSchoolLevel",
    description: `Link a level to a school`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "levelId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/school/:id/unlink-level/:levelId",
    alias: "deleteSchoolLevel",
    description: `Unlink a level from a school`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
      {
        name: "levelId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/school/target-school-groups",
    alias: "findAllTargetSchoolGroups",
    description: `Get all target school groups`,
    requestFormat: "json",
    response: TargetSchoolGroupsEntity,
  },
  {
    method: "get",
    path: "/student-exam-progress/answers/:slug",
    alias: "getStudentAnswersForExam",
    description: `Retrieve student answers for an exam, for all the students the exam has been shared to`,
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(StudentProgressForExam),
  },
  {
    method: "post",
    path: "/student-exam-progress/create-or-update-section-answer",
    alias: "createOrUpdateSectionAnswer",
    description: `Create or update a student section answer. Only students can access this route.`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateOrUpdateStudentSectionAnswerDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/student-exam-progress/submission-result/:submissionId",
    alias: "getSubmissionResult",
    description: `Retrieve submission result for a specific code submission`,
    requestFormat: "json",
    parameters: [
      {
        name: "submissionId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: CodeSubmissionResultEntity,
  },
  {
    method: "post",
    path: "/student-exam-progress/submit-code/:sectionId",
    alias: "submitCodeForSection",
    description: `Execute submitted code for a specific section and return the results`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ sourceCode: z.string() }).strict().passthrough(),
      },
      {
        name: "sectionId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.object({ submissionId: z.number() }).strict().passthrough(),
  },
  {
    method: "get",
    path: "/student-exam-progress/user-answer/:sectionId",
    alias: "getUserAnswerForSection",
    description: `Retrieve student answer for a section`,
    requestFormat: "json",
    parameters: [
      {
        name: "sectionId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: StudentAnswerForSection,
  },
  {
    method: "get",
    path: "/student-exam-progress/user-answers/:slug",
    alias: "getUserAnswersForExam",
    description: `Retrieve the current user answers for an exam`,
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: StudentProgressForExam,
  },
  {
    method: "post",
    path: "/student-exam-sharing/create",
    alias: "createExamSharing",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateStudentExamSharingDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/student-exam-sharing/create-or-update-professor-comment",
    alias: "createOrUpdateProfessorComment",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateOrUpdateProfessorCommentDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/student-exam-sharing/delete/:id",
    alias: "removeExamSharing",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/student-exam-sharing/get-all",
    alias: "getExamSharings",
    requestFormat: "json",
    response: z.array(ExamSharing),
  },
  {
    method: "get",
    path: "/student-exam-sharing/get-all-shared-to-me",
    alias: "getExamsSharedToMe",
    requestFormat: "json",
    response: z.array(ExamSharedToMe),
  },
  {
    method: "get",
    path: "/student-exam-sharing/get-students-to-which-exam-was-shared-section-answers/:sectionId",
    alias: "getStudentsToWhichExamWasSharedSectionAnswers",
    requestFormat: "json",
    parameters: [
      {
        name: "sectionId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.array(StudentAnswerForSharedExamSection),
  },
  {
    method: "patch",
    path: "/student-exam-sharing/update",
    alias: "updateExamSharing",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateStudentExamSharingDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/student-group",
    alias: "createStudentGroup",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateStudentGroupDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/student-group",
    alias: "findAllStudentGroups",
    requestFormat: "json",
    response: z.array(StudentGroup),
  },
  {
    method: "get",
    path: "/student-group/:id",
    alias: "findOneStudentGroup",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: StudentGroup,
  },
  {
    method: "patch",
    path: "/student-group/:id",
    alias: "updateStudentGroup",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateStudentGroupDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/student-group/:id",
    alias: "removeStudentGroup",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/subject",
    alias: "findAllSubjects",
    description: `Get all subjects. A subject is a specific subject. For example, &quot;Informatique&quot;, etc.`,
    requestFormat: "json",
    response: z.array(Subject),
  },
  {
    method: "delete",
    path: "/user",
    alias: "delete",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/user-orders/create-checkout-session",
    alias: "createCheckoutSession",
    requestFormat: "json",
    response: UserOrder,
  },
  {
    method: "post",
    path: "/user-orders/stripe-webhook",
    alias: "processStripeWebhook",
    requestFormat: "json",
    parameters: [
      {
        name: "stripe-signature",
        type: "Header",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/user-orders/update-user-cart",
    alias: "updateUserCart",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateUserCartDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/user-orders/user-cart",
    alias: "getUserCart",
    requestFormat: "json",
    response: z.array(CartItem),
  },
  {
    method: "get",
    path: "/user-orders/user-order/:reference",
    alias: "getUserOrder",
    requestFormat: "json",
    parameters: [
      {
        name: "reference",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserOrder,
  },
  {
    method: "delete",
    path: "/user/:userId",
    alias: "deleteUser",
    requestFormat: "json",
    parameters: [
      {
        name: "userId",
        type: "Path",
        schema: z.number(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/user/all-usernames",
    alias: "getAllUsernames",
    requestFormat: "json",
    parameters: [
      {
        name: "searchText",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(Username),
  },
  {
    method: "get",
    path: "/user/me",
    alias: "getMe",
    requestFormat: "json",
    response: UserProfileInfo,
  },
  {
    method: "patch",
    path: "/user/update-profile",
    alias: "updateProfile",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateProfileDto,
      },
    ],
    response: z.void(),
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
